<template>
  <v2-modal
    :show="show"
    title="Segmentation Books"
    size="lg"
    @close="onClickClose"
  >
    <div :class="{ 'v2-seg-books--disabled': isExportingSegmentationBook }">
      <!-- Main list view -->
      <template v-if="!isImportingSegmentationBook">
        <!-- Loading state -->
        <template v-if="isLoadingSegmentationBooks">
          <div class="v2-seg-books__loading">
            <v2-spinner
              size="md"
              label="Loading segmentation books..."
            />
          </div>
        </template>

        <!-- Main content -->
        <template v-else>
          <!-- Create segmentation book -->
          <div class="v2-seg-books__actions">
            <v2-button
              v-if="!showCreateSegmentationBook"
              variant="primary"
              size="sm"
              @click="showCreateSegmentationBook = true"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line
                  x1="12"
                  y1="5"
                  x2="12"
                  y2="19"
                />
                <line
                  x1="5"
                  y1="12"
                  x2="19"
                  y2="12"
                />
              </svg>
              Create
            </v2-button>
          </div>

          <div
            v-if="showCreateSegmentationBook"
            class="v2-seg-books__create-form"
          >
            <span class="v2-seg-books__create-label">Enter segmentation book name</span>
            <v2-input
              v-model="segmentationBookName"
              placeholder="Enter segmentation book name"
              class="v2-seg-books__create-input"
            />
            <div class="v2-seg-books__create-actions">
              <v2-button
                variant="outline"
                size="sm"
                @click="showCreateSegmentationBook = false"
              >
                Cancel
              </v2-button>
              <v2-button
                variant="primary"
                size="sm"
                :disabled="!segmentationBookName || isCreatingSegmentationBook"
                :loading="isCreatingSegmentationBook"
                @click="createSegmentationBook"
              >
                {{ isCreatingSegmentationBook ? 'Creating...' : 'Create' }}
              </v2-button>
            </div>
          </div>

          <!-- Master Segmentation Book -->
          <div class="v2-seg-books__section">
            <div class="v2-seg-books__section-title">
              Master Segmentation Book
            </div>
            <div class="v2-seg-books__row v2-seg-books__row--bordered">
              <div class="v2-seg-books__row-label">
                Master Segmentation Book
              </div>
              <div class="v2-seg-books__row-actions">
                <v2-button
                  variant="outline"
                  size="sm"
                  :loading="isExportingSegmentationBook && !exportingSegmentationBookId"
                  @click="exportMasterSegmentationBook"
                >
                  <svg
                    v-if="!(isExportingSegmentationBook && !exportingSegmentationBookId)"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line
                      x1="12"
                      y1="3"
                      x2="12"
                      y2="15"
                    />
                  </svg>
                  Export
                </v2-button>
                <v2-button
                  variant="outline"
                  size="sm"
                  @click="onClickImportSegmentationBook()"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line
                      x1="12"
                      y1="15"
                      x2="12"
                      y2="3"
                    />
                  </svg>
                  Import
                </v2-button>
              </div>
            </div>
          </div>

          <!-- All Segmentation Books -->
          <div
            v-if="segmentationBooks.length"
            class="v2-seg-books__section"
          >
            <div class="v2-seg-books__section-title">
              All Segmentation Books ({{ segmentationBooks.length }})
            </div>
            <div class="v2-seg-books__list">
              <div
                v-for="book in segmentationBooks"
                :key="book._id"
                class="v2-seg-books__row"
              >
                <div class="v2-seg-books__book-info">
                  <div class="v2-seg-books__book-name-area">
                    <template v-if="!book.isEditing">
                      <span
                        class="v2-seg-books__book-name"
                        :title="book.segmentationBookName"
                      >
                        {{ book.segmentationBookName }}
                      </span>
                    </template>
                    <template v-else>
                      <div class="v2-seg-books__rename-wrapper">
                        <v2-input
                          :value="book.newName"
                          :error="getRenameError(book)"
                          @input="handleNameInput($event, book)"
                          @keyup.native.esc="cancelRename(book)"
                        />
                        <div class="v2-seg-books__rename-actions">
                          <button
                            v-if="book.newName && validateName(book.newName) && !isRenaming"
                            class="v2-seg-books__rename-btn v2-seg-books__rename-btn--confirm"
                            @click="renameSegmentationBook(book)"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </button>
                          <button
                            v-if="!isRenaming"
                            class="v2-seg-books__rename-btn v2-seg-books__rename-btn--cancel"
                            @click="cancelRename(book)"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <line
                                x1="18"
                                y1="6"
                                x2="6"
                                y2="18"
                              />
                              <line
                                x1="6"
                                y1="6"
                                x2="18"
                                y2="18"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </template>
                  </div>
                  <div class="v2-seg-books__book-actions-inline">
                    <button
                      class="v2-seg-books__icon-btn"
                      title="Rename"
                      @click="startRename(book)"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button
                      class="v2-seg-books__icon-btn v2-seg-books__icon-btn--danger"
                      title="Delete"
                      @click="deleteSegmentationBook(book)"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div class="v2-seg-books__row-actions">
                  <v2-button
                    variant="outline"
                    size="sm"
                    :disabled="isExportingSegmentationBook && exportingSegmentationBookId === book._id"
                    :loading="isExportingSegmentationBook && exportingSegmentationBookId === book._id"
                    @click="exportSegmentationBook(book)"
                  >
                    <svg
                      v-if="!(isExportingSegmentationBook && exportingSegmentationBookId === book._id)"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line
                        x1="12"
                        y1="3"
                        x2="12"
                        y2="15"
                      />
                    </svg>
                    Export
                  </v2-button>
                  <v2-button
                    variant="outline"
                    size="sm"
                    @click="onClickImportSegmentationBook(book)"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line
                        x1="12"
                        y1="15"
                        x2="12"
                        y2="3"
                      />
                    </svg>
                    Import
                  </v2-button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </template>

      <!-- Import file view -->
      <template v-else>
        <p class="v2-seg-books__import-label">
          Upload file for {{ selectedSegmentationBook ? selectedSegmentationBook.segmentationBookName : 'Master Segmentation Book' }}
        </p>

        <!-- File drop zone -->
        <label
          v-if="!segmentationBookFile"
          class="v2-seg-books__dropzone"
        >
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--v2-primary)"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="16 16 12 12 8 16" />
            <line
              x1="12"
              y1="12"
              x2="12"
              y2="21"
            />
            <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
            <polyline points="16 16 12 12 8 16" />
          </svg>
          <span class="v2-seg-books__dropzone-title">Drag & Drop</span>
          <span class="v2-seg-books__dropzone-subtitle">or click to select files from your system</span>
          <input
            type="file"
            accept=".xlsx"
            hidden
            @change="onFileSelected"
          >
        </label>

        <!-- File selected preview -->
        <div
          v-else
          class="v2-seg-books__file-preview"
        >
          <div class="v2-seg-books__file-info">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--v2-success)"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <div class="v2-seg-books__file-details">
              <span class="v2-seg-books__file-name">{{ segmentationBookFile.name }}</span>
              <span class="v2-seg-books__file-size">{{ getSizeInKb(segmentationBookFile.size) }} KB</span>
            </div>
          </div>
          <button
            class="v2-seg-books__file-remove"
            @click="segmentationBookFile = null"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
        </div>
      </template>
    </div>

    <v2-modal
      :show="!!pendingDeleteBook"
      title="Delete Segmentation Book"
      size="sm"
      @close="cancelDeleteSegmentationBook"
    >
      <p class="v2-text-sm">
        Are you sure you want to delete
        <strong v-if="pendingDeleteBook">{{ pendingDeleteBook.segmentationBookName }}</strong>?
        It will be unassigned from the retailer. This action will not impact any of your existing orders.
      </p>
      <template #footer>
        <v2-button
          variant="outline"
          :disabled="isDeletingSegmentationBook"
          @click="cancelDeleteSegmentationBook"
        >
          Cancel
        </v2-button>
        <v2-button
          variant="destructive"
          :disabled="isDeletingSegmentationBook"
          :loading="isDeletingSegmentationBook"
          @click="confirmDeleteSegmentationBook"
        >
          Delete
        </v2-button>
      </template>
    </v2-modal>

    <template #footer>
      <template v-if="isImportingSegmentationBook">
        <v2-button
          variant="outline"
          :disabled="isUploading"
          @click="onClickBackSegmentationBook"
        >
          Back
        </v2-button>
        <v2-button
          variant="primary"
          :disabled="isUploading || !segmentationBookFile"
          :loading="isUploading"
          @click="importSegmentationBook"
        >
          {{ isUploading ? 'Uploading...' : 'Upload' }}
        </v2-button>
      </template>
    </template>
  </v2-modal>
</template>

<script>
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Input from '@/components/v2/V2Input.vue'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import { getSizeInKb, parseErrorObject } from '@/@core/utils/utils'
import { apiToastSuccess } from '@/@core/utils/toast'
import { FILE_FORMATS } from '@/constants'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import {
  CREATE_SEGMENTATION,
  IMPORT_SEGMENTATION,
  GET_SEGMENTATION_BOOKS,
  DELETE_SEGMENTATION_BOOK,
  RENAME_SEGMENTATION,
  DOWNLOAD_MASTER_SEGMENTATION_BOOK,
} from '@/store/modules/collection.module'
import { UPLOAD_DOCUMENTS, DOWNLOAD_FILE } from '@/store/modules/common.module'
import { SHOW_DOWNLOADS_NAV_INDICATOR } from '@/store/modules/notification.module'
import analytics from '@/@core/utils/analytics'

const { FILE_TYPES } = c

export default {
  name: 'V2SegmentationBooksModal',
  components: {
    V2Modal,
    V2Button,
    V2Input,
    V2Spinner,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    collection: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isRenaming: false,
      pendingDeleteBook: null,
      isDeletingSegmentationBook: false,
      showCreateSegmentationBook: false,
      segmentationBookName: '',
      isCreatingSegmentationBook: false,
      isImportingSegmentationBook: false,
      segmentationBookFile: null,
      segmentationBooks: [],
      selectedSegmentationBook: null,
      isExportingSegmentationBook: false,
      exportingSegmentationBookId: null,
      isLoadingSegmentationBooks: false,
      isUploading: false,
      getSizeInKb,
    }
  },
  watch: {
    show(val) {
      if (val) {
        this.loadSegmentationBooks()
      }
    },
  },
  methods: {
    async loadSegmentationBooks() {
      this.isLoadingSegmentationBooks = true
      try {
        const response = await this.$store.dispatch(GET_SEGMENTATION_BOOKS, this.collection._id)
        this.segmentationBooks = response.data.data
      } catch (err) {
        // error toast handled by axios interceptor
      } finally {
        this.isLoadingSegmentationBooks = false
      }
    },

    async createSegmentationBook() {
      this.isCreatingSegmentationBook = true
      try {
        const payload = {
          segmentationBookName: this.segmentationBookName,
        }
        const response = await this.$store.dispatch(CREATE_SEGMENTATION, {
          payload,
          collectionId: this.collection._id,
        })
        apiToastSuccess(response.data.message)
        const res = await this.$store.dispatch(GET_SEGMENTATION_BOOKS, this.collection._id)
        this.segmentationBooks = res.data.data
        this.showCreateSegmentationBook = false
        this.segmentationBookName = null
      } catch (err) {
        // error toast handled by axios interceptor
      } finally {
        this.isCreatingSegmentationBook = false
      }
    },

    startRename(book) {
      this.$set(book, 'isEditing', true)
      this.$set(book, 'newName', book.segmentationBookName)
    },

    async renameSegmentationBook(book) {
      if (this.isRenaming) return
      this.isRenaming = true
      try {
        const payload = { name: book.newName }
        const response = await this.$store.dispatch(RENAME_SEGMENTATION, {
          payload,
          segmentationId: book._id,
        })
        apiToastSuccess(response.data.message)
        const res = await this.$store.dispatch(GET_SEGMENTATION_BOOKS, this.collection._id)
        this.segmentationBooks = res.data.data
        this.$delete(book, 'isEditing')
        this.$delete(book, 'newName')
      } catch (err) {
        // error toast handled by axios interceptor
      } finally {
        this.isRenaming = false
      }
    },

    validateName(name) {
      if (!name || name.trim().length === 0 || name.length > 25) return false
      return true
    },

    getRenameError(book) {
      if (!book.newName) return 'Segmentation Book Name is required'
      if (book.newName.length > 25) return 'Name must be 25 characters or less'
      return ''
    },

    handleNameInput(value, book) {
      if (value.length > 25) {
        this.$set(book, 'newName', value.slice(0, 25))
        return
      }
      this.$set(book, 'newName', value.trim())
    },

    cancelRename(book) {
      this.$delete(book, 'isEditing')
      this.$delete(book, 'newName')
    },

    deleteSegmentationBook(book) {
      this.pendingDeleteBook = book
    },
    cancelDeleteSegmentationBook() {
      if (this.isDeletingSegmentationBook) return
      this.pendingDeleteBook = null
    },
    async confirmDeleteSegmentationBook() {
      if (!this.pendingDeleteBook || this.isDeletingSegmentationBook) return
      this.isDeletingSegmentationBook = true
      try {
        const response = await this.$store.dispatch(DELETE_SEGMENTATION_BOOK, {
          segmentationId: this.pendingDeleteBook._id,
        })
        apiToastSuccess(response.data.message)
        const res = await this.$store.dispatch(GET_SEGMENTATION_BOOKS, this.collection._id)
        this.segmentationBooks = res.data.data
        this.pendingDeleteBook = null
      } catch (err) {
        // error toast handled by axios interceptor
      } finally {
        this.isDeletingSegmentationBook = false
      }
    },

    async exportMasterSegmentationBook() {
      this.isExportingSegmentationBook = true
      const payload = {
        downloadType: 'masterSegmentationBookExport',
        fileType: FILE_FORMATS.XLSX,
        respectiveId: this.collection._id,
      }
      try {
        const res = await this.$store.dispatch(DOWNLOAD_MASTER_SEGMENTATION_BOOK, { payload })
        apiToastSuccess(res.data?.message)
        analytics.track('MASTER_SEGMENTATION_BOOK_EXPORTED_SUCCESSFULLY', { fileObject: res })
        this.$store.commit(SHOW_DOWNLOADS_NAV_INDICATOR)
      } catch (err) {
        analytics.track('MASTER_SEGMENTATION_BOOK_EXPORT_FAILED', { error: parseErrorObject(err) })
      } finally {
        this.isExportingSegmentationBook = false
      }
    },

    exportSegmentationBook(book) {
      this.isExportingSegmentationBook = true
      this.exportingSegmentationBookId = book._id
      const payload = {
        url: this.$kpParams(this.$kpEndpoint.catalog.segmentation.export, book._id),
        fileName: `${book?.segmentationBookName}-segmentation-book.xlsx`,
        fileFormat: FILE_FORMATS.XLSX,
        successMessage: 'Segmentation book exported successfully',
      }
      this.$store.dispatch(DOWNLOAD_FILE, payload)
        .then(() => {
          analytics.track('SEGMENTATION_BOOK_EXPORTED_SUCCESSFULLY', { bookId: book._id })
          this.$store.commit(SHOW_DOWNLOADS_NAV_INDICATOR)
        })
        .catch(err => {
          analytics.track('SEGMENTATION_BOOK_EXPORT_FAILED', { error: parseErrorObject(err) })
        })
        .finally(() => {
          this.isExportingSegmentationBook = false
          this.exportingSegmentationBookId = null
        })
    },

    onClickImportSegmentationBook(book = null) {
      this.selectedSegmentationBook = book
      this.isImportingSegmentationBook = true
    },

    onClickBackSegmentationBook() {
      this.selectedSegmentationBook = null
      this.isImportingSegmentationBook = false
      this.segmentationBookFile = null
    },

    onFileSelected(event) {
      const file = event.target.files[0]
      if (file) {
        this.segmentationBookFile = file
      }
      event.target.value = ''
    },

    async importSegmentationBook() {
      this.isUploading = true
      try {
        const fileLink = await this.$store.dispatch(UPLOAD_DOCUMENTS, {
          fileType: FILE_TYPES.PRICE_BOOK,
          file: this.segmentationBookFile,
        })
        const payloadObject = {
          collectionId: this.collection?._id,
          payload: {
            segmentationBookFile: fileLink,
          },
        }
        if (this.selectedSegmentationBook?._id) {
          payloadObject.segmentationId = this.selectedSegmentationBook._id
        }
        const result = await this.$store.dispatch(IMPORT_SEGMENTATION, payloadObject)
        apiToastSuccess(result.data.message)
        this.isImportingSegmentationBook = false
        this.segmentationBookFile = null
        this.selectedSegmentationBook = null
        analytics.track('SEGMENTATION_BOOK_IMPORTED_SUCCESSFULLY', {
          importedFile: fileLink,
          collection: this.collection,
        })
      } catch (err) {
        analytics.track('SEGMENTATION_BOOK_IMPORT_FAILED', { error: parseErrorObject(err) })
      } finally {
        this.isUploading = false
      }
    },

    closeSegmentationBook() {
      this.selectedSegmentationBook = null
      this.isUploading = false
      this.isCreatingSegmentationBook = false
      this.segmentationBookName = null
      this.isImportingSegmentationBook = false
      this.segmentationBookFile = null
      this.showCreateSegmentationBook = false
    },

    onClickClose() {
      if (this.isImportingSegmentationBook) {
        this.onClickBackSegmentationBook()
        return
      }
      this.closeSegmentationBook()
      this.$emit('close')
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-seg-books {
  &--disabled {
    pointer-events: none;
    opacity: 0.6;
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-8) 0;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    margin-bottom: var(--v2-space-4);
  }

  &__create-form {
    padding: var(--v2-space-4);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    background: var(--v2-muted);
    margin-bottom: var(--v2-space-4);
  }

  &__create-label {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
  }

  &__create-input {
    margin-top: var(--v2-space-2);
  }

  &__create-actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--v2-space-2);
    margin-top: var(--v2-space-3);
  }

  &__section {
    margin-top: var(--v2-space-4);
  }

  &__section-title {
    font-family: var(--v2-font-sans);
    font-weight: 600;
    font-size: 0.875rem;
    color: var(--v2-foreground);
    padding-bottom: var(--v2-space-2);
    border-bottom: 1px solid var(--v2-border);
  }

  &__list {
    max-height: 300px;
    overflow-y: auto;
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--v2-space-3) 0;
    border-top: 1px solid var(--v2-border);

    &--bordered {
      border-bottom: 1px solid var(--v2-border);
    }
  }

  &__row-label {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
  }

  &__row-actions {
    display: flex;
    gap: var(--v2-space-2);
    flex-shrink: 0;
  }

  &__book-info {
    display: flex;
    align-items: center;
    gap: var(--v2-space-2);
    flex: 1;
    min-width: 0;
  }

  &__book-name-area {
    flex: 1;
    min-width: 0;
  }

  &__book-name {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--v2-foreground);
    display: inline-block;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__rename-wrapper {
    position: relative;
    width: 260px;
    max-width: 260px;

    ::v-deep .v2-input-wrapper {
      width: 100%;
    }

    ::v-deep .v2-input {
      width: 100%;
      padding-right: 64px;
      text-overflow: ellipsis;
    }
  }

  &__rename-actions {
    position: absolute;
    top: 19px;
    right: var(--v2-space-2);
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    gap: var(--v2-space-1);
    flex-shrink: 0;
    pointer-events: auto;
  }

  &__rename-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-1);
    border: none;
    border-radius: var(--v2-radius-sm);
    background: transparent;
    cursor: pointer;
    transition: all 0.15s ease;

    &--confirm {
      color: var(--v2-success);

      &:hover {
        background: rgba(16, 185, 129, 0.1);
      }
    }

    &--cancel {
      color: var(--v2-destructive);

      &:hover {
        background: rgba(229, 72, 77, 0.1);
      }
    }
  }

  &__book-actions-inline {
    display: flex;
    align-items: center;
    gap: var(--v2-space-1);
    flex-shrink: 0;
  }

  &__icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-1);
    border: none;
    border-radius: var(--v2-radius-sm);
    background: transparent;
    color: var(--v2-muted-foreground);
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: var(--v2-muted);
      color: var(--v2-foreground);
    }

    &--danger:hover {
      color: var(--v2-destructive);
      background: rgba(229, 72, 77, 0.1);
    }
  }

  &__import-label {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    margin-bottom: var(--v2-space-3);
  }

  &__dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-8) var(--v2-space-4);
    border: 2px dashed var(--v2-border);
    border-radius: var(--v2-radius-lg);
    cursor: pointer;
    transition: all 0.2s ease;
    background: var(--v2-background);

    &:hover {
      border-color: var(--v2-primary);
      background: var(--v2-muted);
    }
  }

  &__dropzone-title {
    font-family: var(--v2-font-sans);
    font-weight: 700;
    font-size: 1rem;
    color: var(--v2-foreground);
    margin-top: var(--v2-space-2);
  }

  &__dropzone-subtitle {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
    margin-top: var(--v2-space-1);
  }

  &__file-preview {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--v2-space-3) var(--v2-space-4);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    background: var(--v2-muted);
  }

  &__file-info {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
  }

  &__file-details {
    display: flex;
    flex-direction: column;
  }

  &__file-name {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-foreground);
  }

  &__file-size {
    font-family: var(--v2-font-sans);
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
  }

  &__file-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-1);
    background: var(--v2-destructive);
    border: none;
    border-radius: var(--v2-radius-sm);
    color: #fff;
    cursor: pointer;
    transition: filter 0.15s ease;

    &:hover {
      filter: brightness(1.1);
    }
  }
}
</style>
